/**
 * บั๊ก & ฟีเจอร์ที่ขอ — รวมจากทุกบริษัท (HR/Owner ส่งผ่าน widget ในแอป).
 * ดูรายละเอียด + รูป/วิดีโอแนบ + เปลี่ยนสถานะ/ใส่โน้ต. Claude Code ดึงตัว status=new มาแก้.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, Lightbulb, ExternalLink } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface FeedbackMedia { path: string; type?: string; name?: string; url?: string | null }
interface FeedbackRow {
  id: string;
  tenant_id: string;
  tenant_name: string | null;
  tenant_slug: string | null;
  reporter_name: string | null;
  reporter_emp_code: string | null;
  kind: 'bug' | 'feature';
  title: string;
  detail: string | null;
  page_url: string | null;
  context: Record<string, unknown> | null;
  media: FeedbackMedia[];
  status: string;
  priority: string | null;
  admin_note: string | null;
  created_at: string;
}

const STATUS = [
  { v: 'new', label: 'ใหม่', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  { v: 'triaged', label: 'รับเรื่องแล้ว', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { v: 'in_progress', label: 'กำลังแก้', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  { v: 'resolved', label: 'เสร็จแล้ว', cls: 'bg-green-50 text-green-700 border-green-200' },
  { v: 'wontfix', label: 'ไม่ทำ', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
];
const statusMeta = (v: string) => STATUS.find((s) => s.v === v) ?? STATUS[0]!;

export function Feedback() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('new');
  const [kind, setKind] = useState('');

  const { data = [], isLoading } = useQuery({
    queryKey: ['platform', 'feedback', status, kind],
    queryFn: () => {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (kind) p.set('kind', kind);
      return apiFetch<FeedbackRow[]>(`/platform/feedback?${p.toString()}`);
    },
    refetchInterval: 60_000,
  });

  const update = useMutation({
    mutationFn: (args: { id: string; patch: Record<string, unknown> }) =>
      apiFetch(`/platform/feedback/${args.id}`, { method: 'PATCH', body: args.patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'feedback'] }),
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-xl font-semibold mb-1">บั๊ก & ฟีเจอร์ที่ขอ</h1>
      <p className="text-sm text-gray-500 mb-4">เรื่องที่ HR/Owner แจ้งเข้ามาจากทุกบริษัท</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm">
          <option value="">ทุกสถานะ</option>
          {STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm">
          <option value="">ทุกประเภท</option>
          <option value="bug">แจ้งปัญหา</option>
          <option value="feature">ขอฟีเจอร์</option>
        </select>
      </div>

      {isLoading ? (
        <p className="text-gray-500 py-8 text-center">กำลังโหลด…</p>
      ) : data.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">ไม่มีรายการ</p>
      ) : (
        <div className="space-y-3">
          {data.map((r) => {
            const sm = statusMeta(r.status);
            return (
              <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {r.kind === 'bug'
                        ? <span className="inline-flex items-center gap-1 text-xs text-rose-600"><Bug className="w-3.5 h-3.5" /> ปัญหา</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Lightbulb className="w-3.5 h-3.5" /> ฟีเจอร์</span>}
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-full border ${sm.cls}`}>{sm.label}</span>
                    </div>
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.tenant_name ?? r.tenant_slug ?? r.tenant_id} · {r.reporter_name ?? r.reporter_emp_code ?? '—'} · {new Date(r.created_at).toLocaleString('th-TH')}
                    </div>
                  </div>
                  <select
                    value={r.status}
                    onChange={(e) => update.mutate({ id: r.id, patch: { status: e.target.value } })}
                    className="border rounded-lg px-2 py-1 text-xs shrink-0"
                  >
                    {STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </div>

                {r.detail && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{r.detail}</p>}

                <div className="text-xs text-gray-400 mt-2 space-y-0.5">
                  {r.page_url && <div>หน้า: <code>{r.page_url}</code></div>}
                  {r.context?.user_agent ? <div className="truncate">อุปกรณ์: {String(r.context.user_agent)}</div> : null}
                </div>

                {r.media.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {r.media.map((m, i) => (
                      <a key={i} href={m.url ?? '#'} target="_blank" rel="noreferrer" className="block">
                        {(m.type ?? '').startsWith('video')
                          ? <video src={m.url ?? undefined} className="w-24 h-24 object-cover rounded border" />
                          : <img src={m.url ?? undefined} alt="" className="w-24 h-24 object-cover rounded border" />}
                      </a>
                    ))}
                  </div>
                )}

                <details className="mt-3">
                  <summary className="text-xs text-gray-500 cursor-pointer">โน้ตทีมงาน</summary>
                  <textarea
                    defaultValue={r.admin_note ?? ''}
                    placeholder="บันทึกภายใน…"
                    rows={2}
                    className="border rounded-lg px-2 py-1 text-sm w-full mt-1"
                    onBlur={(e) => { if (e.target.value !== (r.admin_note ?? '')) update.mutate({ id: r.id, patch: { admin_note: e.target.value } }); }}
                  />
                </details>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
